import { Controller } from '@hotwired/stimulus'
import debouncedResize from '../utils/debounced_resize'

// Rotates a list of phrases inside a headline, owner.com style: each phrase is
// rebuilt word-by-word with a staggered slide-up + fade + de-blur, so the line
// looks like it's being assembled. The wrapper width animates to fit each phrase
// so the rest of the headline shifts gracefully.
//
// Usage:
//   <span data-controller="dynamic-text"
//         data-dynamic-text-words-value='["book more tables","sell event tickets"]'
//         data-dynamic-text-interval-value="2800">
//     <span class="dynamic-text__word text-gradient" data-dynamic-text-target="word">book more tables</span>
//   </span>
//
// The first phrase is rendered server-side so the line reads correctly before /
// without JS. There is no overflow mask — words are invisible while offset, so
// descenders (g, y, …) are never clipped.
export default class extends Controller {
  static values = {
    words: Array,
    interval: { type: Number, default: 2800 },
    stagger: { type: Number, default: 90 }
  }

  // Must cover the last staggered word's exit (delay + opacity duration in SCSS).
  static EXIT_MS = 560

  connect() {
    if (this.wordsValue.length < 1) return

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.index = 0
    // No width is set here: `startRotation` measures it once rotation begins,
    // and a phone never rotates. A width measured on a cold load is read off
    // the fallback font — the web font has not landed on a full page load, only
    // on a Turbo visit — and comes out too narrow, so the phrase wrapped inside
    // its own box once Poppins arrived, with nothing left to re-measure it.
    this.render(this.wordsValue[0], false)
    // Announce the starting phrase so synced visuals (hero showcase) can match it.
    this.announce()

    if (this.wordsValue.length < 2) return

    // Phones show the first phrase and never rotate: the phrases wrap unevenly
    // there, so any rotation either shifts the layout or reserves a hole under
    // the short ones. The rotation starts if the viewport grows past the mark.
    this.wide = window.matchMedia('(min-width: 768px)')
    this.onWideChange = () => (this.wide.matches ? this.startRotation() : this.stopRotation())
    this.wide.addEventListener('change', this.onWideChange)
    if (!this.wide.matches) return

    this.startRotation()
  }

  startRotation() {
    if (this.timer) return
    // `document.fonts.ready` below is a promise that cannot be cancelled, so a
    // narrow viewport that stops rotation before the font lands would otherwise
    // get its reservation written back by a callback with no rotation left to
    // justify it. This flag is what `reserveHeight` checks; `this.timer` cannot
    // be, because the first reservation is taken before the interval is set.
    this.rotating = true
    // Every phrase is a different length, so a longer one can wrap where the
    // one before it didn't — and the headline, and everything under it, jumps a
    // whole line mid-rotation. Reserving the tallest phrase's height up front is
    // what holds the page still. It is measured rather than guessed because the
    // line count depends on the column width, which is a clamp() on the viewport.
    this.reserveHeight()
    if (document.fonts && document.fonts.ready) {
      // Metrics change when the web font lands, so the reservation is taken again.
      document.fonts.ready.then(() => this.reserveHeight())
    }
    this.stopResize = debouncedResize(() => this.reserveHeight())

    // Reduced-motion users still rotate through every phrase so no value prop is
    // lost — they just swap instantly (no slide/fade/blur, no exit delay).
    this.timer = setInterval(() => this.cycle(), this.intervalValue)
  }

  // Every timer and listener the rotation owns. Both teardown paths call this,
  // so a new one cannot be added to `disconnect` and forgotten in `stopRotation`
  // — the leak that shape invites. `clearTimeout(undefined)` is a no-op, so
  // nothing here needs a guard.
  clearTimers() {
    clearInterval(this.timer)
    this.timer = null
    clearTimeout(this.exitTimeout)
    clearTimeout(this.remeasureTimeout)
    if (this.stopResize) this.stopResize()
    this.stopResize = null
  }

  // Back to the first phrase at its own height, with nothing reserved.
  stopRotation() {
    this.clearTimers()
    this.rotating = false
    this.cycling = false
    this.remeasurePending = false
    this.index = 0
    this.element.style.minHeight = ''
    this.element.style.width = ''
    this.render(this.wordsValue[0], false)
    this.announce()
  }

  disconnect() {
    if (this.wide && this.onWideChange) this.wide.removeEventListener('change', this.onWideChange)
    // The exit timeout is scheduled per cycle, so a Turbo visit inside the
    // ~560ms exit window would otherwise render into a detached element.
    this.clearTimers()
  }

  cycle() {
    if (this.reducedMotion) {
      this.index = (this.index + 1) % this.wordsValue.length
      const next = this.wordsValue[this.index]
      this.setWidth(next)
      this.render(next, false)
      this.announce()
      return
    }

    // Stagger the current words out (up + fade + blur).
    this.words().forEach((word, i) => {
      word.style.transitionDelay = `${i * this.staggerValue}ms`
      word.classList.add('is-leaving')
    })

    this.index = (this.index + 1) % this.wordsValue.length
    const next = this.wordsValue[this.index]
    this.announce()

    // The width is deliberately NOT set here. The outgoing words are still on
    // screen, and re-sizing the wrapper under them re-flows a phrase that wraps
    // — it visibly snaps back onto one line as it fades. Sizing with the new
    // words instead keeps each phrase at its own width for the whole of its
    // exit, and the width still animates as the new phrase assembles.
    this.cycling = true
    this.exitTimeout = setTimeout(() => {
      this.setWidth(next)
      this.render(next, true)
      this.cycling = false
      // A re-measure that arrived mid-turn was parked; run it once the phrase
      // that is assembling has finished, so it does not cut that animation off
      // either. If it lands mid-turn again it parks again, so it settles in the
      // first quiet gap.
      if (this.remeasurePending) {
        this.remeasurePending = false
        this.remeasureTimeout = setTimeout(() => this.reserveHeight(), this.constructor.EXIT_MS)
      }
    }, this.constructor.EXIT_MS)
  }

  // Bubbles a `dynamic-text:change` event (default prefix = controller identifier)
  // carrying the current phrase index, so a synced visual elsewhere on the page can
  // react via `data-action="dynamic-text:change@window->…"`.
  announce() {
    this.dispatch('change', { detail: { index: this.index } })
  }

  // Rebuilds the wrapper as one span per word. When `animate`, words start parked
  // below/blurred and cascade into place on the next frame.
  render(phrase, animate) {
    this.element.textContent = ''

    phrase.split(' ').forEach((text, i) => {
      const word = document.createElement('span')
      word.className = 'dynamic-text__word text-gradient'
      word.textContent = text
      if (animate) {
        word.classList.add('is-entering')
        word.style.transitionDelay = `${i * this.staggerValue}ms`
      }
      this.element.appendChild(word)
    })

    if (!animate) return

    // Two frames so the parked state is committed before we transition out of it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.words().forEach((word) => word.classList.remove('is-entering'))
      })
    })
  }

  words() {
    return Array.from(this.element.querySelectorAll('.dynamic-text__word'))
  }

  // Lays out every phrase in turn at the width it will actually be given, keeps
  // the tallest result as a `min-height`, then puts the live phrase back. The
  // reservation is cleared first so a previous, larger one can't hold the
  // measurement open at a width where the phrases no longer wrap.
  reserveHeight() {
    if (!this.element.isConnected) return
    // Rotation stopped (a narrow viewport) clears the reservation deliberately,
    // so a late `document.fonts.ready` callback must not write it back.
    if (!this.rotating) return

    // Measuring rebuilds the word spans, so doing it inside a turn would wipe
    // the `is-leaving` classes off the words that are still fading and assemble
    // the incoming phrase early — it then assembles a second time when the exit
    // timeout fires. `document.fonts.ready` and the debounced resize handler can
    // both land there (iOS fires `resize` on every URL-bar show/hide), so the
    // measure is parked until the turn is over rather than dropped.
    if (this.cycling) {
      this.remeasurePending = true
      return
    }

    const liveIndex = this.index
    const liveTransition = this.element.style.transition
    this.element.style.minHeight = ''
    // Measured with the width transition off: a phrase sized while the wrapper
    // is still easing to its width is laid out at the *previous* phrase's width
    // and wraps, which reserves a line that is never actually needed.
    this.element.style.transition = 'none'

    let tallest = 0
    this.wordsValue.forEach((phrase) => {
      this.render(phrase, false)
      this.setWidth(phrase)
      tallest = Math.max(tallest, this.element.offsetHeight)
    })

    // The live phrase's width is re-measured rather than put back. The first
    // one was taken before the web font landed, and a width read off fallback
    // metrics is too narrow for Poppins: the phrase wrapped onto a second line
    // and the whole headline — and everything under it — sat a line too tall
    // until the first rotation corrected it.
    this.render(this.wordsValue[liveIndex], false)
    this.element.style.width = ''
    this.setWidth(this.wordsValue[liveIndex])
    void this.element.offsetWidth
    this.element.style.transition = liveTransition
    this.element.style.minHeight = `${tallest}px`
  }

  // Animates the wrapper width to fit `phrase` by measuring an off-screen clone.
  setWidth(phrase) {
    const ref = this.words()[0]
    if (!ref) return

    const parts = phrase.split(' ')
    const ghost = document.createElement('span')
    // The words are measured with no spaces between them and the wrapper's own
    // column gap is added back per join. Measuring the phrase with literal
    // spaces instead under-reads it — the flex gap is wider than a space at
    // these sizes — and the wrapper was landing a few pixels short of the line,
    // which dropped the last word onto a second one.
    ghost.textContent = parts.join('')
    const styles = window.getComputedStyle(ref)
    Object.assign(ghost.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      font: styles.font,
      letterSpacing: styles.letterSpacing
    })
    document.body.appendChild(ghost)
    const textWidth = ghost.offsetWidth
    ghost.remove()

    const gap = parseFloat(window.getComputedStyle(this.element).columnGap) || 0
    const wanted = Math.ceil(textWidth + gap * (parts.length - 1)) + 2

    // A phrase wider than its column would otherwise be handed a width the
    // column can't give it: the wrapper clamps at `max-width: 100%` while the
    // width animation still runs out to the larger number, so the line twitches
    // as it settles. Capping at the available width keeps the animation inside
    // what will actually be painted.
    const available = this.element.parentElement
      ? this.element.parentElement.clientWidth
      : 0
    const width = available ? Math.min(wanted, available) : wanted

    // Growing is applied instantly, shrinking still animates. A width that eases
    // *open* holds the incoming phrase inside the outgoing one's narrower box
    // for the length of the transition, and a phrase that doesn't fit wraps —
    // the headline gained a line and lost it again on every turn to a longer
    // phrase. Nothing is on screen at this point (the old words have faded and
    // the new ones have not been built yet), so the jump is not visible.
    const current = parseFloat(this.element.style.width) || 0
    if (width > current) {
      const inlineTransition = this.element.style.transition
      this.element.style.transition = 'none'
      this.element.style.width = `${width}px`
      // Read back to commit the un-transitioned width before restoring.
      void this.element.offsetWidth
      this.element.style.transition = inlineTransition
      return
    }

    this.element.style.width = `${width}px`
  }
}
